import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { LetterToBoardService, LetterOnBoard } from '@schoolbelle/api/letter-to-board';
import { GroupService } from '@schoolbelle/api/group';
import { InfiniteListService } from '@schoolbelle/common/infinite-list';
import { Subscription, of, Observable, merge, Subject } from 'rxjs';
import { defaults } from "lodash";
import { switchMap, filter, map, tap, startWith, share, shareReplay } from 'rxjs/operators';
import { BoardService } from '@schoolbelle/api/board';
import { LetterReadService } from '@schoolbelle/api/letter-read';
import { ActivatedRoute } from '@angular/router';
import { LetterService } from '@schoolbelle/api/letter';


export type LetterListQuries = {
  query?:string;
  survey?:boolean;
  mine?:boolean;
  from?:number;
  to?:number;
};
export const quriesDefault:LetterListQuries = {
  query:'',
  survey:null,
  mine:null,
  from:null,
  to:null,
};

@Component({
  template: '',
})
export class LetterListComponent implements OnInit {
  /**
   * Should fetch an initial chunk programmatically
   */
  public RUN_INITIALLY:boolean = false;
  /**
   * Constant query params to be applied when fetching
   */
  public defaultParams:{[key:string]:any} = {};

  /**
   * Filtering elements
   */
  public queries:LetterListQuries;
  protected queriesChangeEvent = new Subject();  
  protected refreshFixedList = new Subject();

  public _fixed:LetterOnBoard[] = [];
  public fixed:LetterOnBoard[] = [];
  public list:LetterOnBoard[] = [];
  public infinite:InfiniteListService;

  public boardMap = new Map<string, {list:LetterOnBoard[], fixed:LetterOnBoard[], queries:LetterListQuries, infinite:InfiniteListService}>();
  
  public board$ = this.board.board
  .pipe(  
    startWith(this.board.currentBoard),
    filter(board=>board ? true : false),
    tap((board)=>this.fill(board && board.bid)),
    tap(()=>this.cdRef.detectChanges()),
    shareReplay(1),
  );  

  protected subscriptions = new Subscription();

  constructor(
    public group:GroupService,
    public board:BoardService,
    protected letter:LetterService,
    protected ltb:LetterToBoardService,
    protected read:LetterReadService,
    protected route:ActivatedRoute,
    protected cdRef:ChangeDetectorRef,
  ) { 
  }




  ngOnInit() {
    // update general list
    this.subscriptions.add(
      this.board$
      .pipe(
        filter(board=>board ? true : false),
        switchMap(()=>this.loadGeneralList()),
        filter(({infinite})=>this.infinite.id === infinite.id),
      )
      .subscribe(({list})=>{
        this.updateGeneralList(list);
      })
    );

    // update fixed list
    this.subscriptions.add(
      merge(this.board$, this.refreshFixedList.asObservable())
      .pipe(  
        switchMap(()=>this.loadFixedList()),
      )
      .subscribe()
    );    
    
    // update list upon push 
    this.subscriptions.add(
      this.ltb.onNotification
      .pipe(
        filter(({data:{group_id, type}})=>{
          return group_id === this.group.currentGroup.gid 
          && type === this.board.currentBoard.type
        }),
      )
      .subscribe(({data:{event, ltg_id, directUpdate}})=>{
        switch(event) {
          case "insert":
          this.fetchLatest();
          break;
          case "delete":
          this.removeItem({ltg_id});
          break;
          case "update":
          this.refreshItem({ltg_id});
          break;
          case "directUpdate":
          this.updateItem(ltg_id, directUpdate);
          break;

        }

      })
    );

  }
  ngOnDestroy () {
    this.subscriptions.unsubscribe();
  }

  defaultListFilter (item):boolean { 
    return item.letter_id  && !this.isInFixedList(item)? true : false 
  };
  queryListFilter (item):boolean {
    const {query, survey, mine} = this.queries;
    if (query || survey || mine) {
      const shouldSearchName = true;
      const shouldSearchTitle = this.board.currentBoardData.display === 'short';
      return (
        !query || (
          0
          || (shouldSearchName && item.author_name && item.author_name.match(query) ? true : false)
          || (shouldSearchTitle && item.title && item.title.match(query) ? true : false) 
          || (item.content && item.content.replace(/(<([^>]+)>)/gi, "").match(query) ? true : false)
        )
      )
      && (
        typeof survey !== 'boolean' || (
          (survey && item.survey_id_g3)
          || (!survey && !item.survey_id_g3))
      )
      && (
        typeof mine !== 'boolean' || (
          (mine && item.created_by === this.group.currentGroup.uid)
          || (!mine && item.created_by !== this.group.currentGroup.uid))
      );
    }
    else {
      return true;
    }     
  }  

  /**
   * Update board related properties upon switching boards
   * @param bid 
   */
  fill (bid:string) {
    let stored = this.boardMap.get(bid);
    if (!stored) {
      const infinite = new InfiniteListService<LetterOnBoard>();
      stored = {
        list:[],
        fixed:[], 
        queries:quriesDefault,
        infinite,
      };
      this.boardMap.set(bid, stored);
    }
    else if (stored.infinite.list.length) {
      this.scrollHasHitTop();
    }
    this.list = stored.list;
    this.fixed = stored.fixed;
    this.queries = stored.queries;
    this.infinite = stored.infinite;      
    
    this.infinite.filter = this.filter.bind(this);
    this.infinite.map = this.map.bind(this);
  }
  map (li:any):any {
    return li;
  }
  filter (li:any):boolean {
    return this.defaultListFilter(li) && this.queryListFilter(li) ? true : false;
  }  
  
  updateGeneralList (list:LetterOnBoard[]) {
    this.list = list;
    this.cdRef.detectChanges();
  }
  updateFixedList (list:LetterOnBoard[]) {
    this.fixed = list;
    this.cdRef.detectChanges();
  }
  

  trackByInternalId (index, item) {
    return item.internal_id;
  }
  search (params:{query?:string, survey?:boolean, mine?:boolean, from?:number, to?:number} = {}) {
    const queries = defaults(params, quriesDefault); 
    const queries_old = this.queries;
    const {query, survey, mine, from, to} = queries;

    if (query || survey || mine) {
      this.defaultParams.size = 100;
      this.infinite.max_size = 100;
      this.infinite.stopped = false;
    }
    else {
      this.defaultParams.size = 10;
      this.infinite.max_size = 10;
    } 
    this.queries = queries; 
    this.queriesChangeEvent.next(queries);
    
    if (queries_old.from !== from || queries_old.to !== to) {
      this.infinite.updateQueryParams({from, to});
    }
    else {
      this.infinite.refresh();
      if (this.infinite.list.length < 10) this.infinite.loadMore();
    }

  }
  fetchLatest () {
    return this.infinite.loadLatest()    
  }  
  removeItem (item:{ltg_id:number}) {
    return this.infinite.remove({ltg_id:item.ltg_id});
  }
  /**
   * update a matched item first in infinite list and then in component list too.
   * Infinite list may have a "map" function provided.
   * In that case, the matched item in component list must be replaced  with the one from the infinite list.
   * 
   * @param ltg_id 
   * @param data 
   * @returns 
   */
  updateItem (ltg_id:number, data:{[key:string]:any}) {
    const index_in_infinite_list = this.infinite.update({ltg_id}, data);
    const index_in_component_list = this.list.findIndex(li=>li.ltg_id === ltg_id);
    if (index_in_infinite_list !== -1 && index_in_component_list !== -1) this.list[index_in_component_list] = this.infinite.list[index_in_infinite_list];
    return index_in_component_list;
  }
  markAsRead (item:LetterOnBoard):Observable<any> {
    return this.read.markAsRead(item);
  }  

  loadGeneralList () {
    const infinite = this.infinite;
    return this.infinite.getListObservable(
      this.ltb.fetch.bind(this.ltb), 
      'ltg_id', 
      this.defaultParams, 
      {inital_run:this.RUN_INITIALLY}
    )
    .pipe(
      map(list=>{ return {infinite, list}; }),
    )  
  }
  


 
  loadFixedList ():Observable<any[]|undefined> {
    const infinite = this.infinite;
    return this.ltb.fetch({fixed:true})
    .pipe(
      map(list=>list.filter(li=>li.letter_id).slice(0, 3)),
      switchMap((list)=>{
        this._fixed = list; 
        return this.queriesChangeEvent.asObservable().pipe(startWith(this.queries),map(()=>this._fixed));
      }),
      map((list)=>list.filter(li=>this.queryListFilter(li))),
      map((list)=>list.map(li=>this.map(li))),
      map(list=>{
        if (this.infinite.id !== infinite.id) return undefined;
        this.updateFixedList(list);
        this.infinite.refresh();
        return list;
      }),
    )
  }


  refreshItem (item:{ltg_id:number}) {
    let wasFixed:boolean, isFixed:boolean;
    const ob = of(null).pipe(
      switchMap(()=>{
        return this.ltb.fetch({ltg_id:item.ltg_id});    
      }),
      map(list=>list[0]),
      switchMap(async (li)=>{

        const old = this.infinite._list.find(item=>item.ltg_id === li.ltg_id);

        wasFixed = this.fixed.find(item=>item.ltg_id === li.ltg_id) ? true :false;
        if (wasFixed || li.fixed_for) console.log(await this.loadFixedList().toPromise());
        isFixed = this.fixed.find(item=>item.ltg_id === li.ltg_id) ? true :false;

        if (isFixed && wasFixed) { // still stays in the fixed list. Do nothing.
          console.debug('still stays in the fixed list.');
        }
        else if (isFixed) { // moves from general list to fixed list
          const indexRemoved = this.infinite.remove(old);
          console.debug('moves from general list to fixed list. remove:', indexRemoved);
          
        } 
        else if (wasFixed) { // moves from fixed list to general list
          const indexAdded = this.infinite.add(li);
          console.debug('moves from fixed list to general list. add:', indexAdded);
        }
        else {
          const indexReplaced = this.infinite.replace(old, li);
          console.debug('stays in the general list. replace:', indexReplaced);
        }
        return li;
      }),
      share(),
    );
    ob.subscribe();
    return ob;
  } 

  isInFixedList (item):boolean {
    return this.fixed.find(li=>li.letter_id === item.letter_id) ? true : false;  
  }

  fixAtTopFor (item:LetterOnBoard, time?:number) {
    let fixed_for:number;
    if (time) { // attach
      // const created_at = item.created_at; // item.created_at;
      // const fixed_until = Date.now() + time;
      // fixed_for = fixed_until - created_at;
      fixed_for = time;
    }
    else { // detach
      fixed_for = 0;
    }
    return this.letter.write({letter_id:item.letter_id, fixed_for})
    .pipe(
      switchMap(()=>this.letter.send({board_type:item.type, letter_id: item.letter_id, targets:item.targets} as any)),
      tap(()=>{
        // apply it locally right away.
        if (time) { // attach
          this.fixed.splice(0, 0, {...item, fixed_for});
          this.infinite.refresh();
        }
        else { // detach
          this.fixed.splice(this.fixed.findIndex(li=>li.ltg_id === item.ltg_id), 1);
          this.infinite.refresh();
        }        
      })
    )
  }

  scrollHasHitBottom () {
    this.infinite.scrollHasHitBottom();
  }
  scrollHasHitTop () {
    this.infinite.scrollHasHitTop();
  } 
}
